import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Check, Search, X } from "lucide-react";
import GlideMenu from "./GlideMenu";

export type CustomerOption = { cust_code: string; cust_name: string };

interface CustomerWorkspaceMenuProps {
  position: { top: number; left: number };
  onClose: () => void;
  customers?: CustomerOption[];
  selectedCustId?: string;
  onSelectCustomer?: (code: string) => void;
  userType?: string | null;
  currentName: string;
}

export default function CustomerWorkspaceMenu({ position, onClose, customers = [], selectedCustId, onSelectCustomer, userType, currentName }: CustomerWorkspaceMenuProps) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return customers;
    const s = q.toLowerCase();
    return customers.filter((c) => c.cust_name.toLowerCase().includes(s) || c.cust_code.toLowerCase().includes(s));
  }, [customers, q]);

  return createPortal(
    <div
      data-workspace-menu
      className="fixed z-50 w-72 rounded-[14px] bg-surface p-1.5 shadow-overlay border border-line"
      style={{ top: position.top, left: position.left, animation: "pop-in 180ms cubic-bezier(0.23,1,0.32,1) both", transformOrigin: "top left" }}
    >
      <div className="flex items-center gap-2 px-2.5 py-2 mb-1 border-b border-line">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] font-semibold text-ink leading-tight">{currentName}</p>
        </div>
      </div>

      {userType === "Internal" && customers.length > 0 ? (
        <>
          {customers.length > 5 && (
            <div className="px-1 mb-1">
              <div className="flex h-8 items-center gap-1.5 rounded-[8px] bg-field px-2 text-ink-3 shadow-hairline">
                <Search size={14} className="shrink-0" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search customers..."
                  className="min-w-0 flex-1 bg-transparent text-[12px] font-medium text-ink outline-none placeholder:text-ink-3"
                  autoFocus
                />
                {q && (
                  <button type="button" onClick={() => setQ("")} className="flex size-5 items-center justify-center rounded text-ink-3 hover:text-ink">
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="max-h-60 overflow-y-auto py-0.5">
            <GlideMenu className="flex flex-col gap-px" highlightClassName="inset-x-0 rounded-[8px] bg-hover-2">
              {filtered.map((cust) => {
                const isSelected = cust.cust_code === selectedCustId;
                return (
                  <button
                    key={cust.cust_code}
                    data-menu-row
                    type="button"
                    onClick={() => {
                      onSelectCustomer?.(cust.cust_code);
                      onClose();
                    }}
                    className={`relative z-10 flex h-9 w-full items-center gap-2 rounded-[8px] px-2 text-left transition-colors ${
                      isSelected ? "bg-hover-2/70" : ""
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink">{cust.cust_name}</span>
                    <span className="shrink-0 text-[11px] font-mono text-ink-3 mr-1">{cust.cust_code}</span>
                    {isSelected && <span className="shrink-0 text-[#3B3E81]"><Check size={18} /></span>}
                  </button>
                );
              })}
              {filtered.length === 0 && <div className="px-3 py-3 text-center text-[12px] text-ink-3">No customers match</div>}
            </GlideMenu>
          </div>
        </>
      ) : (
        <div className="px-2 py-1 text-[12px] text-ink-3">Customer: <span className="font-semibold text-ink">{currentName}</span></div>
      )}
    </div>,
    document.body
  );
}